import type * as pdfjsLib from 'pdfjs-dist'
import * as React from 'react'

import { PdfLinkOverlay, PdfOutlineItem } from './pdf-viewer.types'
import {
  buildLinkOverlays,
  getPdfOutline,
  loadPdfDocument,
  renderPdfPage,
  resolvePdfDestinationPage,
} from './pdfjs-gateway'

const MIN_SCALE = 0.5
const MAX_SCALE = 3
const SCALE_STEP = 0.25

export function usePdfViewerController(params: { sourceUrl: string; onLoadingComplete?: () => void }) {
  const { onLoadingComplete, sourceUrl } = params

  const containerRef = React.useRef<HTMLDivElement>(null)
  const loadingTaskRef = React.useRef<pdfjsLib.PDFDocumentLoadingTask | null>(null)
  const pdfDocRef = React.useRef<pdfjsLib.PDFDocumentProxy | null>(null)

  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(0)
  const [scale, setScale] = React.useState(1)
  const [outline, setOutline] = React.useState<PdfOutlineItem[] | null>(null)
  const [tocOpen, setTocOpen] = React.useState(false)
  const [settingsOpen, setSettingsOpen] = React.useState(true)
  const [pageInput, setPageInput] = React.useState('1')
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const [alignment, setAlignment] = React.useState<'left' | 'center' | 'right'>('center')
  const [theme, setTheme] = React.useState<'light' | 'dark' | 'system'>('light')
  const [renderKey, setRenderKey] = React.useState(0)
  const [reloadNonce, setReloadNonce] = React.useState(0)

  const queueRender = React.useCallback(() => {
    setRenderKey(prev => prev + 1)
  }, [])

  const clampPage = React.useCallback(
    (value: number) => {
      if (!totalPages) {
        return 1
      }
      return Math.min(Math.max(value, 1), totalPages)
    },
    [totalPages]
  )

  const goToPage = React.useCallback(
    (value: number) => {
      const target = clampPage(value)
      setCurrentPage(target)
      setPageInput(String(target))
      setError(null)
      queueRender()
    },
    [clampPage, queueRender]
  )

  const goToFirstPage = React.useCallback(() => goToPage(1), [goToPage])
  const goToLastPage = React.useCallback(() => goToPage(totalPages || 1), [goToPage, totalPages])
  const goToNextPage = React.useCallback(() => goToPage(currentPage + 1), [currentPage, goToPage])
  const goToPreviousPage = React.useCallback(() => goToPage(currentPage - 1), [currentPage, goToPage])

  const zoomIn = React.useCallback(() => {
    setScale(prev => Math.min(prev + SCALE_STEP, MAX_SCALE))
    queueRender()
  }, [queueRender])

  const zoomOut = React.useCallback(() => {
    setScale(prev => Math.max(prev - SCALE_STEP, MIN_SCALE))
    queueRender()
  }, [queueRender])

  const setZoom = React.useCallback(
    (nextScale: number) => {
      const safeScale = Math.min(Math.max(nextScale, MIN_SCALE), MAX_SCALE)
      setScale(safeScale)
      queueRender()
    },
    [queueRender]
  )

  const handlePageInputChange = React.useCallback((value: string) => {
    setPageInput(value)
  }, [])

  const commitPageInput = React.useCallback(() => {
    const page = Number.parseInt(pageInput, 10)
    if (Number.isNaN(page)) {
      setPageInput(String(currentPage))
      return
    }
    goToPage(page)
  }, [currentPage, goToPage, pageInput])

  const toggleFullscreen = React.useCallback(async () => {
    if (!containerRef.current) {
      return
    }

    if (document.fullscreenElement) {
      await document.exitFullscreen()
      return
    }

    await containerRef.current.requestFullscreen()
  }, [])

  React.useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }

    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  React.useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        setLoading(true)
        setError(null)

        if (loadingTaskRef.current) {
          loadingTaskRef.current.destroy()
          loadingTaskRef.current = null
        }

        if (pdfDocRef.current) {
          pdfDocRef.current.destroy()
          pdfDocRef.current = null
        }

        const { loadingTask, pdfDocument } = await loadPdfDocument(sourceUrl)
        loadingTaskRef.current = loadingTask

        if (cancelled) {
          pdfDocument.destroy()
          return
        }

        pdfDocRef.current = pdfDocument
        setTotalPages(pdfDocument.numPages)
        setCurrentPage(1)
        setPageInput('1')
        setScale(1)
        setLoading(false)
        queueRender()
        onLoadingComplete?.()

        const extractedOutline = await getPdfOutline(pdfDocument)
        if (!cancelled) {
          setOutline(extractedOutline)
        }
      } catch (loadError) {
        if (cancelled) {
          return
        }
        setLoading(false)
        setError('Failed to load this PDF. Please verify the source URL is accessible.')
      }
    }

    load()

    return () => {
      cancelled = true
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy()
        loadingTaskRef.current = null
      }
    }
  }, [onLoadingComplete, queueRender, reloadNonce, sourceUrl])

  React.useEffect(() => {
    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy()
      }
    }
  }, [])

  const renderCurrentPage = React.useCallback(
    async (canvas: HTMLCanvasElement): Promise<PdfLinkOverlay[]> => {
      if (!pdfDocRef.current) {
        return []
      }

      const { annotations, viewport } = await renderPdfPage({
        pdfDocument: pdfDocRef.current,
        pageNumber: currentPage,
        scale,
        canvas,
      })

      return buildLinkOverlays({ annotations, viewport })
    },
    [currentPage, scale]
  )

  const navigateToDestination = React.useCallback(
    async (destination: string | unknown[] | null) => {
      if (!pdfDocRef.current) {
        return
      }

      const targetPage = await resolvePdfDestinationPage({
        pdfDocument: pdfDocRef.current,
        destination,
        totalPages,
      })

      if (!targetPage) {
        return
      }

      goToPage(targetPage)
    },
    [goToPage, totalPages]
  )

  const retry = React.useCallback(() => {
    setError(null)
    setLoading(true)
    if (loadingTaskRef.current) {
      loadingTaskRef.current.destroy()
      loadingTaskRef.current = null
    }
    if (pdfDocRef.current) {
      pdfDocRef.current.destroy()
      pdfDocRef.current = null
    }
    setOutline(null)
    setTotalPages(0)
    setCurrentPage(1)
    setPageInput('1')
    setScale(1)
    setReloadNonce(prev => prev + 1)
  }, [])

  React.useEffect(() => {
    if (loading || error) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') {
        return
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPreviousPage()
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNextPage()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [error, goToNextPage, goToPreviousPage, loading])

  return {
    containerRef,
    loading,
    error,
    currentPage,
    totalPages,
    scale,
    tocOpen,
    settingsOpen,
    outline,
    pageInput,
    isFullscreen,
    alignment,
    theme,
    renderKey,
    canGoPrevious: currentPage > 1,
    canGoNext: currentPage < totalPages,
    canZoomIn: scale < MAX_SCALE,
    canZoomOut: scale > MIN_SCALE,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    zoomIn,
    zoomOut,
    setZoom,
    toggleFullscreen,
    setTocOpen,
    setSettingsOpen,
    setAlignment,
    setTheme,
    handlePageInputChange,
    commitPageInput,
    renderCurrentPage,
    navigateToDestination,
    retry,
  }
}
